import api from '../../../services/axios';
import { handleApiError } from '../../../services/errorHandler';
import { prepareDateForAPI } from '../../../utils/formatDate';
import {
  IFuelsIndicatorsBiodiesel,
  ICreateFuelsIndicatorsBiodieselDTO,
  IPricesOverviewAndVariationResponse,
} from './types';

const BASE_URL = '/fuels-indicators-biodiesel';

export const getFuelsIndicatorsBiodiesel = async (): Promise<IFuelsIndicatorsBiodiesel[]> => {
  try {
    const response = await api.get<IFuelsIndicatorsBiodiesel[]>(BASE_URL);
    return response.data;
  } catch (error) {
    console.error('Error fetching fuels indicators biodiesel:', error);
    throw handleApiError(error);
  }
};

export const createFuelsIndicatorsBiodiesel = async (
  data: ICreateFuelsIndicatorsBiodieselDTO
): Promise<IFuelsIndicatorsBiodiesel> => {
  try {
    // Garantir que todos os campos estejam no formato camelCase para a API
    const apiData = {
      productLinesId: data.productLinesId,
      openingDate: data.openingDate,
      closingDate: data.closingDate,
      region: data.region,
      referencePrice: data.referencePrice,
      periodVariation: data.periodVariation,
      monthlyVariation: data.monthlyVariation,
      refineryDeficit: data.refineryDeficit,
      source: data.source,
      observations: data.observations,
    };

    const response = await api.post<IFuelsIndicatorsBiodiesel>(BASE_URL, apiData);
    return response.data;
  } catch (error) {
    console.error('Error creating fuels indicators biodiesel:', error);
    throw handleApiError(error);
  }
};

export const deleteFuelsIndicatorsBiodiesel = async (productLinesId: string): Promise<void> => {
  try {
    await api.delete(`${BASE_URL}/${productLinesId}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteFuelsIndicatorsBiodieselById = async (id: string): Promise<void> => {
  try {
    await api.delete(`${BASE_URL}/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Deleta um indicador de biodiesel por filtros
 *
 * @param productLinesId ID da linha de produto
 * @param openingDate Data de abertura no formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param closingDate Data de fechamento no formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param region Região (Norte, Nordeste, etc)
 */
export const deleteFuelsIndicatorsBiodieselByRegion = async (
  productLinesId: string,
  openingDate: string,
  closingDate: string,
  region: string
): Promise<void> => {
  try {
    await api.delete(
      `${BASE_URL}/by-filters/${productLinesId}/${openingDate}/${closingDate}/${region}`
    );
  } catch (error: any) {
    // Se for erro 404 com mensagem específica, não é considerado erro crítico
    if (
      error.response?.status === 404 &&
      error.response?.data?.message?.includes('Nenhum indicador de biodiesel encontrado')
    ) {
      console.warn(`Região ${region} não encontrada para exclusão.`);
      return; // Não propaga o erro
    }
    throw handleApiError(error);
  }
};

/**
 * Busca um indicador de biodiesel específico para obter seu ID
 *
 * @param productLinesId ID da linha de produto
 * @param openingDate Data de abertura no formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param closingDate Data de fechamento no formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param region Região (Norte, Nordeste, etc)
 * @returns O indicador encontrado ou null se não existir
 */
export const getFuelsIndicatorsBiodieselByRegion = async (
  productLinesId: string,
  openingDate: string,
  closingDate: string,
  region: string
): Promise<IFuelsIndicatorsBiodiesel | null> => {
  try {
    const response = await api.get<IFuelsIndicatorsBiodiesel[]>(
      `${BASE_URL}/by-filters?productLinesId=${productLinesId}&openingDate=${openingDate}&closingDate=${closingDate}&region=${region}`
    );

    if (response.data && response.data.length > 0) {
      return response.data[0];
    }

    return null;
  } catch (error) {
    console.error(`Erro ao buscar indicador para região ${region}:`, error);
    return null;
  }
};

/**
 * Atualiza um indicador de biodiesel existente usando o endpoint PATCH
 *
 * @param id ID do indicador a ser atualizado
 * @param data Dados a serem atualizados
 * @returns O indicador atualizado
 */
export const updateFuelsIndicatorsBiodiesel = async (
  id: string,
  data: Partial<ICreateFuelsIndicatorsBiodieselDTO>
): Promise<IFuelsIndicatorsBiodiesel> => {
  try {
    // Converter para camelCase para a API
    const apiData: any = {};

    if (data.productLinesId !== undefined) apiData.productLinesId = data.productLinesId;
    if (data.openingDate !== undefined) apiData.openingDate = data.openingDate;
    if (data.closingDate !== undefined) apiData.closingDate = data.closingDate;
    if (data.region !== undefined) apiData.region = data.region;
    if (data.referencePrice !== undefined) apiData.referencePrice = data.referencePrice;
    if (data.periodVariation !== undefined) apiData.periodVariation = data.periodVariation;
    if (data.monthlyVariation !== undefined) apiData.monthlyVariation = data.monthlyVariation;
    if (data.refineryDeficit !== undefined) apiData.refineryDeficit = data.refineryDeficit;
    if (data.source !== undefined) apiData.source = data.source;
    if (data.observations !== undefined) apiData.observations = data.observations;

    const response = await api.patch<IFuelsIndicatorsBiodiesel>(`${BASE_URL}/${id}`, apiData);
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar indicador de biodiesel:`, error);
    throw handleApiError(error);
  }
};

/**
 * Atualiza um indicador de biodiesel por região ou cria se não existir
 *
 * @param productLinesId ID da linha de produto
 * @param openingDate Data de abertura no formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param closingDate Data de fechamento no formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param region Região (Norte, Nordeste, etc)
 * @param data Dados atualizados do indicador
 */
export const updateFuelsIndicatorsBiodieselByRegion = async (
  productLinesId: string,
  openingDate: string,
  closingDate: string,
  region: string,
  data: Partial<ICreateFuelsIndicatorsBiodieselDTO>
): Promise<IFuelsIndicatorsBiodiesel> => {
  try {
    // Primeiro, buscar o indicador para obter o ID
    const existingIndicator = await getFuelsIndicatorsBiodieselByRegion(
      productLinesId,
      openingDate,
      closingDate,
      region
    );

    // Se o indicador existir, atualizar usando PATCH
    if (existingIndicator) {
      // Verificar se o campo data.newDates existe, o que significa que queremos atualizar as datas
      if (data.newDates) {
        // Criar uma cópia do objeto sem o campo newDates
        const { newDates, ...dataWithoutNewDates } = data;

        return updateFuelsIndicatorsBiodiesel(existingIndicator.id, {
          ...dataWithoutNewDates,
          openingDate: new Date(prepareDateForAPI(newDates.openingDate)),
          closingDate: new Date(prepareDateForAPI(newDates.closingDate)),
        });
      }

      return updateFuelsIndicatorsBiodiesel(existingIndicator.id, data);
    }

    // Se não existir, criar um novo
    return createFuelsIndicatorsBiodiesel({
      ...data,
      productLinesId,
      openingDate: new Date(prepareDateForAPI(openingDate)),
      closingDate: new Date(prepareDateForAPI(closingDate)),
      region: region as any,
      // Garantir que valores não definidos sejam zero para criar o registro
      referencePrice: data.referencePrice ?? 0,
      periodVariation: data.periodVariation ?? 0,
      monthlyVariation: data.monthlyVariation ?? 0,
      refineryDeficit: data.refineryDeficit ?? 0,
      source: data.source ?? '',
    } as ICreateFuelsIndicatorsBiodieselDTO);
  } catch (error) {
    console.error(`Erro ao processar indicador para região ${region}:`, error);
    throw handleApiError(error);
  }
};

/** Obtém a data mais recente disponível */
export const getMaxDateFromDatabase = async (): Promise<string> => {
  try {
    const response = await api.get(`${BASE_URL}/max-date`);
    return response.data.maxDate;
  } catch (error) {
    console.error('Erro ao buscar data máxima:', error);
    throw handleApiError(error);
  }
};

/**
 * Navega para a próxima data disponível baseada na unidade de tempo especificada
 *
 * @param baseDate Data de referência para navegação (formato YYYY-MM-DD)
 * @param direction Direção da navegação (prev/next)
 * @param unit Unidade de tempo (day/week/month) - padrão: day
 * @param amount Quantidade de unidades - padrão: 1
 * @param region Filtro opcional por região
 * @returns Objeto com nextDate e hasNextDate
 */
export const getNextAvailableDate = async (
  baseDate: string,
  direction: 'prev' | 'next',
  unit: 'day' | 'week' | 'month' = 'day',
  amount: number = 1,
  region?: string
): Promise<{ nextDate: string; hasNextDate: boolean }> => {
  try {
    const params = new URLSearchParams({
      baseDate,
      direction,
      unit,
      amount: amount.toString(),
    });

    if (region) {
      params.append('region', region);
    }

    const response = await api.get(`${BASE_URL}/next-available-date?${params.toString()}`);

    return response.data;
  } catch (error) {
    console.error('Erro ao buscar próxima data disponível:', error);
    throw handleApiError(error);
  }
};

export const getPricesOverviewAndVariation = async (params: {
  baseDate?: string;
  region?: string;
  quantity?: number;
  productLinesId?: string;
}): Promise<IPricesOverviewAndVariationResponse[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.baseDate) {
      queryParams.append('baseDate', params.baseDate);
    }
    if (params.region) {
      queryParams.append('region', params.region);
    }
    if (params.quantity) {
      queryParams.append('quantity', params.quantity.toString());
    }
    if (params.productLinesId) {
      queryParams.append('productLinesId', params.productLinesId);
    }

    const response = await api.get<IPricesOverviewAndVariationResponse[]>(
      `${BASE_URL}/prices-overview-and-variation?${queryParams.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de preços e variações:', error);
    throw handleApiError(error);
  }
};
